import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, RefreshCw, RotateCcw } from "@digital-twins/design-system/icons";

import { ChatComposer } from "@/components/chat/ChatComposer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/Text";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TwinsMarkLoader } from "@/components/quick-study-results/TwinsMarkLoader";
import { AnalyticsEvents, track } from "@/lib/services/analytics";
import { AnalystElapsed } from "./AnalystElapsed";
import { AnalystMessage } from "./AnalystMessage";
import { StorylineHeader } from "./StorylineHeader";
import { useAnalystTab } from "./useAnalystTab";
import { storylineWasUpdated } from "@/features/studies/report/storyline";
import { assertNeverStorylineState } from "@/features/studies/report/reportViewState";

import { ANALYST_SUGGESTION_IDS } from "@digital-twins/shared-types";

import type { ReactNode } from "react";
import type { AnalystSuggestionId } from "@digital-twins/shared-types";
import type {
  MetricResult,
  QualitativeThemePackage,
} from "@digital-twins/shared-types/reporting";
import type { StorylineViewState } from "@/features/studies/report/reportViewState";

/**
 * `owner`: the storyline header above the owner's analyst thread (chat,
 * chips, reset). `readOnly`: the storyline alone, for teammates and share
 * links; no composer, chips, reset or thinking row.
 */
export type AnalystTabMode = "owner" | "readOnly";

interface AnalystTabProps {
  experimentId: string;
  mode: AnalystTabMode;
  storyline: StorylineViewState;
  metricResults?: Readonly<Record<string, MetricResult>> | null;
  /** Theme packages, for storyline plots of coded themes. */
  qualitativeResults?: Readonly<Record<string, QualitativeThemePackage>> | null;
  /** The study name: the storyline title's fallback. */
  studyName?: string | null;
  /** Pager numbers (visible index + 1) by sequence item id, for slide links. */
  slideNumbers?: ReadonlyMap<string, number>;
  /** Opens the Deck tab at a sequence item. */
  onOpenSlide?: (slideId: string) => void;
}

const NO_SLIDE_NUMBERS: ReadonlyMap<string, number> = new Map();

export function AnalystTab(props: AnalystTabProps) {
  const { mode } = props;

  switch (mode) {
    case "owner":
      return <AnalystOwnerThread {...props} />;
    case "readOnly":
      return <AnalystStorylineReadOnly {...props} />;
    default: {
      const unhandled: never = mode;
      return unhandled;
    }
  }
}

/** The storyline alone: read by teammates and share-link viewers. */
function AnalystStorylineReadOnly(props: AnalystTabProps) {
  const {
    storyline,
    metricResults,
    qualitativeResults,
    studyName,
    slideNumbers = NO_SLIDE_NUMBERS,
    onOpenSlide,
  } = props;

  return (
    <section
      className="flex min-h-0 flex-1 flex-col py-4"
      data-testid="analyst-tab"
      data-mode="readOnly"
    >
      <div
        className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col overflow-y-auto py-5 scrollbar-overlay"
        data-testid="analyst-message-list"
      >
        <StorylineHeader
          state={storyline}
          metricResults={metricResults}
          qualitativeResults={qualitativeResults}
          studyName={studyName}
          slideNumbers={slideNumbers}
          onOpenSlide={onOpenSlide}
        />
      </div>
    </section>
  );
}

interface SuggestedQuestion {
  id: AnalystSuggestionId;
  label: string;
  question: string;
}

/** Each chip's label and question copy, by its id. */
const SUGGESTION_COPY_KEYS = {
  key_findings: {
    label: "studyReport.analyst.suggestionKeyFindings",
    question: "studyReport.analyst.suggestionKeyFindingsQuestion",
  },
  crosstab: {
    label: "studyReport.analyst.suggestionCrosstab",
    question: "studyReport.analyst.suggestionCrosstabQuestion",
  },
  demographics: {
    label: "studyReport.analyst.suggestionDemographics",
    question: "studyReport.analyst.suggestionDemographicsQuestion",
  },
} as const satisfies Record<AnalystSuggestionId, { label: string; question: string }>;

const SUGGESTION_CHIP_IDLE_CLASS =
  "rounded-full border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50";
const SUGGESTION_CHIP_SELECTED_CLASS =
  "rounded-full border border-transparent px-3 py-1.5 text-sm bg-primary/10 text-foreground disabled:cursor-not-allowed disabled:opacity-50";

function AnalystOwnerThread(props: AnalystTabProps) {
  const {
    experimentId,
    storyline,
    metricResults,
    qualitativeResults,
    studyName,
    slideNumbers = NO_SLIDE_NUMBERS,
    onOpenSlide,
  } = props;
  const { t } = useTranslation("app");
  const suggestedQuestions: Array<SuggestedQuestion> = ANALYST_SUGGESTION_IDS.map(
    (id) => ({
      id,
      label: t(SUGGESTION_COPY_KEYS[id].label),
      question: t(SUGGESTION_COPY_KEYS[id].question),
    }),
  );

  const [isResetOpen, setIsResetOpen] = useState(false);
  // The chip clicked in this page view, before its message is on record.
  const [explicitSuggestionId, setExplicitSuggestionId] =
    useState<AnalystSuggestionId | null>(null);
  const messageEndRef = useRef<HTMLDivElement>(null);

  const {
    messages,
    storylineRevision: threadStorylineRevision,
    isLoading,
    isSending,
    isReconciling,
    isResetting,
    error,
    sendError,
    canRetrySend,
    loadSession,
    sendMessage,
    retryLastSend,
    retryMessage,
    resetThread,
    isPolling,
  } = useAnalystTab(experimentId);

  const inFlight = isSending || isReconciling || isPolling;
  const showThinking =
    isSending &&
    !isReconciling &&
    messages[messages.length - 1]?.role === "user";
  const inlineError = sendError ?? (messages.length > 0 ? error : null);
  const isStoryLoading = storyline.kind === "storyline_loading";
  const showStoryUpdated =
    storyline.kind === "storyline_ready" &&
    messages.length > 0 &&
    storylineWasUpdated(threadStorylineRevision, storyline.storyline.revision);

  // After a reload the selected chip is the one the thread's first chip
  // message was sent from: the server stores its id on the user message.
  const recordedSuggestionId =
    messages.find((message) => message.role === "user" && message.suggestionId)
      ?.suggestionId ?? null;
  const selectedSuggestionId = explicitSuggestionId ?? recordedSuggestionId;

  useEffect(() => {
    setIsResetOpen(false);
    track(AnalyticsEvents.REPORT_ANALYST_TAB_OPENED, {
      experiment_id: experimentId,
    });
  }, [experimentId]);

  useEffect(() => {
    messageEndRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [messages, isSending]);

  function handleSuggestion(suggestion: SuggestedQuestion) {
    // First selection wins for the session: later chip clicks are follow-ups
    // and must not steal the selected highlight.
    if (selectedSuggestionId === null) {
      setExplicitSuggestionId(suggestion.id);
    }
    void sendMessage(suggestion.question, { suggestionId: suggestion.id });
  }

  async function handleReset() {
    await resetThread();
    setExplicitSuggestionId(null);
    setIsResetOpen(false);
  }

  const suggestionChips = (
    <div className="mt-3 flex flex-wrap gap-2" data-testid="analyst-suggestions">
      {suggestedQuestions.map((suggestion, index) => {
        const isSelected = suggestion.id === selectedSuggestionId;
        return (
          <button
            key={suggestion.id}
            type="button"
            aria-pressed={isSelected}
            className={
              isSelected
                ? SUGGESTION_CHIP_SELECTED_CLASS
                : SUGGESTION_CHIP_IDLE_CLASS
            }
            onClick={() => handleSuggestion(suggestion)}
            disabled={inFlight || isResetting}
            data-testid={`analyst-suggestion-${index}`}
          >
            {suggestion.label}
          </button>
        );
      })}
    </div>
  );

  // Below the storyline header: the asks after a ready story, nothing while
  // it is put together, today's welcome row without one.
  let threadHead: ReactNode;
  switch (storyline.kind) {
    case "storyline_ready":
      threadHead = (
        <div className="border-t border-border pt-4">
          <Text size="sm" color="muted">
            {t("studyReport.storyline.askAbout")}
          </Text>
          {suggestionChips}
        </div>
      );
      break;
    case "storyline_loading":
      threadHead = null;
      break;
    case "storyline_failed":
    case "storyline_malformed":
      threadHead = (
        <div
          className="max-w-prose text-base leading-[1.7] text-foreground"
          data-testid="analyst-welcome-row"
        >
          <Text>{t("studyReport.analyst.emptyBody")}</Text>
          {suggestionChips}
        </div>
      );
      break;
    default:
      threadHead = assertNeverStorylineState(storyline);
  }

  if (isLoading) {
    return (
      <div
        className="flex flex-1 flex-col gap-4 py-6"
        data-testid="analyst-loading"
      >
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-24 w-3/4" />
        <Skeleton className="h-24 w-2/3 self-end" />
        <Skeleton className="mt-auto h-28 w-full" />
      </div>
    );
  }

  if (error && messages.length === 0) {
    return (
      <div
        className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center"
        data-testid="analyst-load-error"
      >
        <AlertCircle className="h-6 w-6 text-destructive" aria-hidden="true" />
        <Text size="sm" color="muted">
          {error}
        </Text>
        <Button
          type="button"
          variant="outline"
          onClick={loadSession}
          data-testid="analyst-load-retry"
        >
          <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
          {t("studyReport.analyst.tryAgain")}
        </Button>
      </div>
    );
  }

  return (
    <section
      className="flex min-h-0 flex-1 flex-col py-4"
      data-testid="analyst-tab"
    >
      <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col">
        {/* Reset is hidden, not disabled, while the thread is empty: a lone
            icon-only control with nothing to act on reads as a mystery
            affordance rather than a quiet one. The tooltip carries the same
            wording as the aria-label so the icon is never the only cue. */}
        <div className="flex h-9 shrink-0 justify-end pb-2">
          {messages.length > 0 && (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsResetOpen(true)}
                    disabled={isResetting || inFlight}
                    aria-label={t("studyReport.analyst.resetAria")}
                    data-testid="analyst-reset-button"
                  >
                    <RotateCcw className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <Text size="sm">{t("studyReport.analyst.resetAria")}</Text>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        <div
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto py-5 scrollbar-overlay"
          data-testid="analyst-message-list"
        >
          {/* The storyline is the thread's header; without one (failed,
              malformed or a report from before storylines) the welcome row
              keeps today's empty state. Both persist for the whole session. */}
          <StorylineHeader
            state={storyline}
            metricResults={metricResults}
            qualitativeResults={qualitativeResults}
            studyName={studyName}
            slideNumbers={slideNumbers}
            onOpenSlide={onOpenSlide}
          />
          {threadHead}

          {showStoryUpdated ? (
            <Text
              size="sm"
              color="muted"
              className="leading-snug"
              data-testid="storyline-updated-note"
            >
              {t("studyReport.storyline.storyUpdated")}
            </Text>
          ) : null}

          {/* Only the conversation is announced; the storyline and the
              welcome row above it are static reading. */}
          <div
            className="flex flex-col gap-4"
            aria-live="polite"
            data-testid="analyst-thread-live"
          >
            {messages.map((message) => (
              <AnalystMessage
                key={message.token}
                message={message}
                onRetry={retryMessage}
              />
            ))}
            {showThinking ? (
              <div
                className="flex items-center gap-2.5"
                data-testid="analyst-thinking"
              >
                <TwinsMarkLoader className="h-4 w-4 text-muted-foreground" />
                <Text size="sm" color="muted">
                  {t("studyReport.analyst.thinking")}
                </Text>
                <AnalystElapsed
                  active
                  startedAt={messages[messages.length - 1]?.createdAt ?? null}
                />
              </div>
            ) : null}
          </div>
          <div ref={messageEndRef} />
        </div>

        {inlineError ? (
          <div
            className="mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive"
            data-testid="analyst-inline-error"
          >
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{inlineError}</span>
            {canRetrySend ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={retryLastSend}
                data-testid="analyst-send-retry"
              >
                {t("studyReport.analyst.retry")}
              </Button>
            ) : null}
          </div>
        ) : null}

        <ChatComposer
          onSend={sendMessage}
          disabled={inFlight || isResetting}
          // While the story is put together, sending waits in a calm,
          // non-busy state (no "Sending" spinner).
          sendBlockedHint={
            isStoryLoading ? t("studyReport.storyline.loadingTitle") : undefined
          }
          placeholder={t("studyReport.analyst.composerPlaceholder")}
          dataTestId="analyst-composer"
          textareaDataTestId="analyst-composer-input"
          flat
          collapsible
        />
      </div>

      <AlertDialog open={isResetOpen} onOpenChange={setIsResetOpen}>
        <AlertDialogContent data-testid="analyst-reset-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("studyReport.analyst.resetTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("studyReport.analyst.resetDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="analyst-reset-cancel">
              {t("studyReport.analyst.resetCancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReset}
              disabled={isResetting || inFlight}
              data-testid="analyst-reset-confirm"
            >
              {t("studyReport.analyst.resetConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
